import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCourse } from '../../services/courses';
import CourseCreationWizard from './wizard/CourseCreationWizard';
import type { WizardInitialData } from './wizard/useWizardState';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

export default function CourseEditPage() {
  const { id: courseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, activeMembership } = useAuth();
  const { theme } = useTheme();

  const { data: courseData, isLoading, error: fetchError } = useCourse(courseId);

  // ─── RBAC check ────────────────────────────────────────────────────
  const allowedRoles = ['Owner', 'Admin', 'Trainer'];
  const userRole = activeMembership?.role;
  const rbacError = !user
    ? 'You must be logged in to edit courses'
    : (!userRole || !allowedRoles.includes(userRole))
      ? 'You do not have permission to edit this course'
      : null;

  // ─── Error states ──────────────────────────────────────────────────
  const displayError = rbacError
    || (fetchError?.message?.includes('404') ? 'Course not found' : null)
    || (fetchError?.message?.includes('403') ? 'You do not have permission to edit this course' : null)
    || (fetchError ? (fetchError.message || 'Failed to load course') : null);

  if (!isLoading && (displayError || !courseData || !courseId)) {
    return (
      <div className="min-h-screen bg-background-primary flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">
            {displayError === 'Course not found' ? 'Course Not Found' : 'Access Denied'}
          </h2>
          <p className="text-text-secondary mb-6">{displayError || 'Course not found'}</p>
          <button
            onClick={() => navigate('/courses')}
            className="px-5 py-2.5 text-sm font-bold bg-primary-500 rounded-full hover:bg-primary-600 dark:hover:bg-primary-400 transition-colors"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <phantom-ui {...phantomProps(theme === 'dark', true)}>
        <div className="min-h-screen bg-background-primary">
          {/* Wizard shell placeholder */}
          <div className="h-16 w-full" />
          <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
            <div className="h-10 w-64 rounded-xl" />
            <div className="h-[400px] rounded-2xl" />
            <div className="flex justify-between">
              <div className="h-10 w-28 rounded-xl" />
              <div className="h-10 w-28 rounded-xl" />
            </div>
          </div>
        </div>
      </phantom-ui>
    );
  }

  // ─── Build initial wizard data from existing course ────────────────
  // courseData is guaranteed non-null here: the isLoading guard returns early above,
  // and the !courseData guard also returns early above.
  const initialData: WizardInitialData = {
    courseId: courseId!,
    title: courseData!.title,
    description: courseData!.description,
    image_url: courseData!.image_url,
    estimated_duration_minutes: courseData!.estimated_duration_minutes,
    layout: courseData!.layout,
    certification_type: courseData!.certification_type,
    certificate_file_key: courseData!.certificate_file_key,
  };

  // ─── Render the wizard in edit mode ────────────────────────────────
  return <CourseCreationWizard initialData={initialData} />;
}
